import { callApi, callStreamApi } from "utils/ContentoApi";
import { SessionLogEntry } from "components/composerv2/components/toolkit/CreateAI/sessionLog";
import {
  AgentMessage,
  AgentStepResponse,
  ComposerSnapshot,
  ProfileChooser,
  parseStepEvent,
  parseStreamedEvent
} from "components/composerv2/components/toolkit/CreateAI/protocol";

const VISUAL_JOB_POLL_INTERVAL_MS = 3000;
const VISUAL_JOB_MAX_WAIT_MS = 6 * 60 * 1000;
const VISUAL_JOB_FAILED_POLLS_ALLOWED = 3; // a deploy can restart the API while a visual is being created

export interface ComposerAgentStepBody {
  messages: AgentMessage[];
  snapshot: ComposerSnapshot;
  previousSession?: SessionLogEntry[];
  postKey?: string; // lets the scheduler find the Willow Create post behind the composer
  profileId?: string; // the profile picked in the chat; the scheduler falls back to the Create post's own, then the newest
  profileChosenBy?: ProfileChooser; // absent while profileId is the default
  createEnabled: boolean; // false leaves Willow Create's profiles and visuals out of the chat
  chatId: string; // one per composer opening, so the scheduler's step records group into chats
}

interface StepCallbacks {
  onText: (text: string) => void; // each fragment of the reply as it streams in
  signal?: AbortSignal; // closes the stream, which makes the server cancel the step
}

interface StepOutcome {
  result?: AgentStepResponse;
  failure?: Error;
}

interface ComposerAgentRequest {
  method: "get" | "post" | "put";
  url: string;
  params?: Record<string, unknown>;
  data?: unknown;
}

const stepUrl = (accountId: string) => `/accounts/${accountId}/composer-agent/step`;

const sendToLoginOnExpiredSession = (status: unknown) => {
  if (status === 401) window.location.href = "/login";
};

// callApi's redirects stay off because its 403 one sends the whole app home, closing the composer and losing the unsaved post when the chat is refused.
export const callComposerAgentApi = async <TResult>(request: ComposerAgentRequest): Promise<TResult> => {
  try {
    return await callApi(request, false, true);
  } catch (err) {
    sendToLoginOnExpiredSession((err as { status?: unknown } | null)?.status);
    throw err;
  }
};

export const isRefusal = (err: unknown): boolean => (err as { status?: unknown } | null)?.status === 403;

const readFailure = async (response: Response): Promise<Error> => {
  const payload = await response.json().catch(() => null);
  return Object.assign(new Error(payload?.message ?? `The request failed with status ${response.status}`), { status: response.status });
};

const rejectFailedOpen = async (response: Response) => {
  if (response.ok) return;
  sendToLoginOnExpiredSession(response.status);
  throw await readFailure(response);
};

export const stepComposerAgent = async (accountId: string, body: ComposerAgentStepBody, { onText, signal }: StepCallbacks): Promise<AgentStepResponse> => {
  const outcome: StepOutcome = {};
  await callStreamApi(
    {
      method: "POST",
      url: stepUrl(accountId),
      data: body,
      retryOnError: false, // a retry would run the model again
      openWhenHidden: true, // a hidden tab must not abort and resend the step
      signal
    },
    {
      onOpen: rejectFailedOpen,
      onMessage: (raw: string) => {
        // The scheduler's keep-alive comments reach here as empty messages.
        if (raw === "") return;
        const event = parseStepEvent(raw);
        if (event.type === "text") onText(event.text);
        else if (event.type === "result") outcome.result = event.result;
        else outcome.failure = new Error(event.message);
      }
    }
  );
  if (outcome.failure) throw outcome.failure;
  if (!outcome.result) throw new Error("The reply ended before a result arrived");
  return outcome.result;
};

// The slower composer-agent calls answer as an event stream with keep-alive comments, so the ingress doesn't cut them off
// after 60 seconds without a byte.
export const callComposerAgentStream = async <TResult>(url: string, data: unknown, signal?: AbortSignal): Promise<TResult> => {
  // The stream ignores a signal that aborted before it started.
  if (signal?.aborted) throw new Error("The composer closed");
  const outcome: { result?: TResult; failure?: Error } = {};
  await callStreamApi(
    { method: "POST", url, data, retryOnError: false, openWhenHidden: true, signal },
    {
      onOpen: rejectFailedOpen,
      onMessage: (raw: string) => {
        if (raw === "") return;
        const event = parseStreamedEvent<TResult>(raw);
        if (event.type === "result") outcome.result = event.result;
        else outcome.failure = new Error(event.message);
      }
    }
  );
  // A result that arrives as the composer closes would otherwise be applied to the next post it opens.
  if (signal?.aborted) throw new Error("The composer closed");
  if (outcome.failure) throw outcome.failure;
  if (outcome.result === undefined) throw new Error("The response ended before a result arrived");
  return outcome.result;
};

export interface CreatedVisual {
  html: string;
  fileName: string;
  preview: { url: string; mimeType: string };
}

type CreateVisualStatus =
  | { status: "running" }
  | { status: "done"; visual: CreatedVisual }
  | { status: "refused" | "failed"; message: string };

const readVisualJob = async (accountId: string, jobId: string): Promise<CreateVisualStatus | null> => {
  try {
    return await callComposerAgentApi<CreateVisualStatus>({ method: "get", url: `/accounts/${accountId}/composer-agent/visual/create/${jobId}` });
  } catch (err) {
    console.error("Failed to check on the visual being created", { accountId, jobId, err });
    return null;
  }
};

// The scheduler creates the visual in a background job, which takes one to three minutes.
// Closing the composer stops the polling; the job itself runs to the end.
export const waitForCreatedVisual = async (accountId: string, jobId: string, signal?: AbortSignal): Promise<CreatedVisual> => {
  const deadlineMillis = Date.now() + VISUAL_JOB_MAX_WAIT_MS;
  let failedPolls = 0;
  while (Date.now() < deadlineMillis && !signal?.aborted) {
    await new Promise(resolve => setTimeout(resolve, VISUAL_JOB_POLL_INTERVAL_MS));
    const job = await readVisualJob(accountId, jobId);
    // The composer can close during the read; a visual that finished then must not land in the next post.
    if (signal?.aborted) break;
    failedPolls = job ? 0 : failedPolls + 1;
    if (failedPolls >= VISUAL_JOB_FAILED_POLLS_ALLOWED) throw new Error("Lost track of the visual being created.");
    if (job?.status === "done") return job.visual;
    if (job && job.status !== "running") throw new Error(job.message);
  }
  throw new Error(signal?.aborted ? "The composer closed before the visual was ready." : "Creating the visual took too long.");
};

export interface ComposerAgentProfile {
  id: string;
  name: string | null;
  language: string | null;
  website: string | null; // the address a website profile was made from
  logo: string | null; // the light theme's logo, a URL or inline SVG
  createdAt: string; // ISO 8601
}

export interface ComposerAgentProfiles {
  profiles: ComposerAgentProfile[]; // newest first
  createProfileId: string | null; // the profile the Create post behind the composer was written with
}

export const fetchComposerAgentProfiles = (accountId: string, postKey?: string): Promise<ComposerAgentProfiles> =>
  callComposerAgentApi({ method: "get", url: `/accounts/${accountId}/composer-agent/profiles`, params: postKey ? { postKey } : undefined });

export interface ComposerAgentSession {
  postKey: string;
  log: SessionLogEntry[];
  updatedAt: string | null;
}

const sessionUrl = (accountId: string, postKey: string) => `/accounts/${accountId}/composer-agent/sessions/${encodeURIComponent(postKey)}`;

export const fetchComposerAgentSession = (accountId: string, postKey: string): Promise<ComposerAgentSession> =>
  callComposerAgentApi({ method: "get", url: sessionUrl(accountId, postKey) });

export const saveComposerAgentSession = (accountId: string, postKey: string, log: SessionLogEntry[]): Promise<ComposerAgentSession> =>
  callComposerAgentApi({ method: "put", url: sessionUrl(accountId, postKey), data: { log } });

export type ComposerAgentEventName = "chat_shown" | "message_sent" | "tool_result" | "error_shown" | "post_saved";

export interface ComposerAgentEvent {
  chatId: string;
  postKey?: string; // absent while the post is unsaved
  name: ComposerAgentEventName;
  properties?: Record<string, unknown>; // counts, names and timings, never the conversation text
}

export const recordComposerAgentEvent = (accountId: string, event: ComposerAgentEvent): Promise<void> =>
  callComposerAgentApi({ method: "post", url: `/accounts/${accountId}/composer-agent/events`, data: event });
